import React, { useState } from "react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { FileText } from "lucide-react";
import countryCode from "../../Data/countryCode.json";
import { createLeadSubmissionId, ingestLead } from "../../lib/leadIngestion";

interface FormProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  campaign?: string;
  universities?: string[];
}
export default function FeatureUniApplyForm({
  onSuccess,
  onError,
  campaign,
  universities,
}: FormProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    let newErrors: { [key: string]: string } = {};

    const name = (formData.get("name") as string)?.trim();
    const phone = (formData.get("phone-number") as string)?.trim();
    const email = (formData.get("email") as string)?.trim();
    const preferredUniversity = (
      formData.get("preferredUniversity") as string
    )?.trim();
    const interestedCourses = (
      formData.get("interestedCourses") as string
    )?.trim();
    const selectedCountryCode = (
      formData.get("country-code") as string
    )?.trim();

    // Find the country name from the country code
    const selectedCountry = countryCode.find(
      (country) => country.countryCode === selectedCountryCode
    );

    if (!/^[A-Za-z]+( [A-Za-z]+)*$/.test(name)) {
      newErrors.name = "Name must only contain letters and spaces.";
    }
    if (!/^\d+$/.test(phone)) {
      newErrors.phone = "Phone number must contain numbers only.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email address.";
    }

    const file = formData.get("uploadedResult") as File;
    if (!file || !file.name || file.size === 0) {
      newErrors.uploadedResult = "Please attach your SPM/UEC results.";
    } else if (file.type !== "application/pdf") {
      newErrors.uploadedResult = "Only PDF files are allowed.";
    } else if (file.size > 5 * 1024 * 1024) {
      newErrors.uploadedResult = "File size must not exceed 5MB.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      console.warn("❌ Validation failed:", newErrors);
      return;
    }

    setLoading(true);

    try {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];

      const storage = getStorage();
      const fileRef = ref(storage, `leads/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const fileURL = await getDownloadURL(fileRef);

      const leadData = {
        name,
        phoneNumber: phone,
        email,
        interestedCourses,
        status: "Unassigned",
        leadSource: "Website",
        campaign: campaign,
        preferredUniversity,
        country: selectedCountry?.countryName || "Malaysia",
        countryCode: selectedCountryCode ? `+${selectedCountryCode}` : "+60",
        createdDate: formattedDate,
        uploadedResult: fileURL,
      };

      await ingestLead(leadData, {
        type: "website",
        externalSubmissionId: createLeadSubmissionId(),
        campaign: leadData.campaign || "",
        submittedAt: new Date().toISOString(),
      });

      form.reset();
      setSelectedFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (onSuccess) onSuccess("Your information has been successfully sent!");
    } catch (err: any) {
      console.error("🔥 Firestore write FAILED:", err);

      // ✅ THIS is the important part
      if (err?.code === "permission-denied") {
        console.error("⛔ BLOCKED BY FIRESTORE SECURITY RULES");
      }

      if (err?.code === "unauthenticated") {
        console.error("⛔ USER NOT AUTHENTICATED - AUTH REQUIRED");
      }

      const errorMsg =
        err instanceof Error ? err.message : "An unexpected error occurred.";

      if (onError) onError(`Failed to submit: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border-4 border-white/50">
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            Full Name *
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name}</p>
            )}
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-200 focus:border-orange-400 transition-all outline-none font-semibold"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            Phone Number *
            {errors.phone && (
              <p className="text-red-500 text-sm">{errors.phone}</p>
            )}
          </label>
          <div className="flex flex-row gap-1">
            <select
              name="country-code"
              defaultValue="60"
              required
              className="w-1/2 px-2 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-200 focus:border-pink-400 transition-all outline-none font-semibold"
            >
              {countryCode.map((country) => (
                <option value={country.countryCode} className="">
                  {country.countryName} (+{country.countryCode})
                </option>
              ))}
            </select>
            <input
              id="phone-number"
              name="phone-number"
              type="tel"
              required
              className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-200 focus:border-pink-400 transition-all outline-none font-semibold"
              placeholder=""
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            Email Address *
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </label>

          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-400 transition-all outline-none font-semibold"
            placeholder=""
          />
        </div>

        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            SPM/UEC Results (PDF only) *
            {errors.uploadedResult && (
              <p className="text-red-500 text-sm">{errors.uploadedResult}</p>
            )}
          </label>

          {/* Custom File Input */}
          <label className="flex items-center justify-center w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-200 focus:border-orange-400 transition-all outline-none font-semibold">
            <span>{selectedFileName ? "Change File" : "Choose File"}</span>
            <input
              ref={fileInputRef}
              id="uploadedResult"
              name="uploadedResult"
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFileName(file.name);
                } else {
                  setSelectedFileName("");
                }
              }}
            />
          </label>

          <p className="text-sm mt-1 text-center">
            {selectedFileName ? (
              <span className="flex items-center justify-center gap-1 font-semibold text-blue-600">
                <FileText className="w-5 h-5" /> {selectedFileName}
              </span>
            ) : (
              <span className="italic text-gray-400">No file chosen</span>
            )}
          </p>
        </div>

        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            Interested Course *
          </label>
          <input
            id="interestedCourses"
            name="interestedCourses"
            type="text"
            required
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-200 focus:border-pink-400 transition-all outline-none font-semibold"
            placeholder="Business, Engineering, IT..."
          />
        </div>

        <div>
          <label className="block text-sm font-black text-gray-700 mb-2">
            Preferred University *
          </label>
          <select
            id="preferredUniversity"
            name="preferredUniversity"
            defaultValue=""
            required
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-200 focus:border-pink-400 transition-all outline-none font-semibold"
          >
            <option value="" disabled>
              Please Select
            </option>
            {universities?.map((university) => (
              <option key={university} value={university}>
                {university}
              </option>
            ))}
          </select>
          {errors.preferredUniversity && (
            <p className="text-red-500 text-sm">{errors.preferredUniversity}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full text-base md:text-lg bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white font-black py-5 rounded-xl shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 cursor-pointer"
        >
          {loading ? "Submitting..." : "🚀 Submit & Get FREE Counselling"}
        </button>
      </form>

      {/* Urgency Message */}
      <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border-2 border-red-200">
        <p className="text-center text-red-600 font-black text-sm flex items-center justify-center gap-2">
          <span className="text-lg animate-pulse">⏳</span>
          Limited scholarship slots left — Apply now!
        </p>
      </div>

      {/* Trust Indicators */}
      <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-600 font-semibold">
        <div className="flex items-center gap-1">
          <span>🔒</span> Secure
        </div>
        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        <div className="flex items-center gap-1">
          <span>✓</span> Free Service
        </div>
        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        <div className="flex items-center gap-1">
          <span>⚡</span> Fast Reply
        </div>
      </div>
    </div>
  );
}
